import {
	DISPLAY_PREFERENCE_DEBOUNCE_MS,
	orgDisplayPreferenceScope,
	tenantDisplayPreferenceScope,
	writeDisplayPreferencePage,
} from '@/lib/displayPreferences'
import { isOrganizationLevelRoute } from '@/lib/routeScope'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore, useCurrentOrganizationIdFromState, useCurrentTenant } from '@/stores/authStore'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Persists a single UI display preference (selected columns, grouping, tab order, ...) to the
 * current user's server-side settings, scoped to the active project, or to the org on an org-level
 * route. Replaces the old localStorage-per-page pattern, which was wiped on every tenant switch and
 * shared across tenants.
 */
export function useDisplayPreference<T>(pageKey: string, prefKey: string, defaultValue: T): [T, (value: T) => void] {
	const orgId = useCurrentOrganizationIdFromState()
	const tenantId = useCurrentTenant()
	// An org-level route has no tenant, so it scopes to the org instead: without this its preferences
	// either fail to persist, or land in whichever project the store happens to still hold. Read from
	// the URL rather than the router, since a route change remounts the page that owns this hook.
	const isOrgLevel = isOrganizationLevelRoute(window.location.pathname)
	const scopeKey = orgId
		? isOrgLevel
			? orgDisplayPreferenceScope(orgId)
			: tenantId
				? tenantDisplayPreferenceScope(orgId, tenantId)
				: null
		: null

	const storedValue = useAuthStore(state => {
		const user = state.user
		if (!scopeKey || !user || !isPlatformUser(user)) {
			return undefined
		}
		const pageBag = user.settings?.displayPreferences?.[scopeKey]?.[pageKey]
		return pageBag?.[prefKey] as T | undefined
	})

	// Callers commonly pass a fresh literal (e.g. `[]`) as defaultValue on every render; capture
	// it once so it can't cause the resync effect below to re-fire (and loop) on every render.
	const defaultValueRef = useRef(defaultValue)
	const [localValue, setLocalValue] = useState<T>(() =>
		storedValue !== undefined ? storedValue : defaultValueRef.current,
	)

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const hasPendingWriteRef = useRef(false)

	const flush = useCallback(
		(value: T) => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				timerRef.current = null
			}
			hasPendingWriteRef.current = false
			writeDisplayPreferencePage(scopeKey, pageKey, { [prefKey]: value })
		},
		[scopeKey, pageKey, prefKey],
	)

	const localValueRef = useRef(localValue)
	localValueRef.current = localValue

	// Flush a pending write on scope change or unmount. Declared before the resync effect
	// below so its cleanup clears hasPendingWriteRef first, before resync checks it.
	useEffect(
		() => () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				flush(localValueRef.current)
			}
		},
		[flush],
	)

	// Resync local value from the store on scope changes or server-value updates, unless a
	// debounced write for the current edit is still pending. `scopeKey` is in the deps
	// explicitly: if the old and new scope happen to have the same (e.g. both undefined)
	// stored value, `storedValue` alone wouldn't change and this would otherwise never re-run.
	// biome-ignore lint/correctness/useExhaustiveDependencies: scopeKey isn't read in the body, but must force a rerun on scope change
	useEffect(() => {
		if (hasPendingWriteRef.current) {
			return
		}
		setLocalValue(storedValue !== undefined ? storedValue : defaultValueRef.current)
	}, [scopeKey, storedValue])

	const setValue = useCallback(
		(value: T) => {
			setLocalValue(value)
			hasPendingWriteRef.current = true
			if (timerRef.current) {
				clearTimeout(timerRef.current)
			}
			timerRef.current = setTimeout(() => flush(value), DISPLAY_PREFERENCE_DEBOUNCE_MS)
		},
		[flush],
	)

	return [localValue, setValue]
}
